import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../../context/UserProvider";
import { db } from "../../../firebase";
import { groupChallengesByStatus } from "../../UserPage/ChallengesPage/challengePageUtils";
import RaceReadyChecklist from "./RaceReadyChecklist";
import { canSnoozeHomeStrip, getRaceReadyProgress, getRaceReadySteps, isHomeStripSnoozed } from "./raceReadySteps";
import { saveRaceReady, trackRaceReady } from "./raceReadyStore";
import { getWelcomeTheme } from "./welcomeThemes";

// Same lookup order as the confirmation page and the dashboard's picture tool.
function getImagePreset(race, raceData, legDetails) {
  return (
    race?.raceInfo?.metadata?.imagePreset ||
    race?.imagePreset ||
    raceData?.metadata?.imagePreset ||
    raceData?.imagePreset ||
    legDetails?.metadata?.imagePreset ||
    legDetails?.imagePreset ||
    ""
  );
}

async function loadRace(race) {
  const [productDoc, legDoc] = await Promise.all([
    getDoc(doc(db, "stripe_products", race.raceId)),
    getDoc(doc(db, "races", race.legId)),
  ]);
  const raceData = productDoc.data() || {};
  return { legId: race.legId, raceData, imagePreset: getImagePreset(race, raceData, legDoc.data()) };
}

const NEXT_STEP_LABELS = {
  device: "Connect your watch or app",
  picture: "Make your challenge picture",
  bib: "Download your BIB",
  dashboard: "Open your dashboard",
};

function getProgress(race, loaded, linkedDevice) {
  const bibLink = loaded.raceData?.metadata?.bib_link;
  const steps = getRaceReadySteps({
    raceReady: race?.raceReady || {},
    linkedDevice,
    hasBib: Boolean(bibLink && bibLink !== "#"),
  });
  const required = steps.filter((step) => !step.optional);
  return { ...getRaceReadyProgress(steps), required, nextStepId: required.find((step) => !step.done)?.id };
}

// Bottom sheet on phones, centred window on computers. Closes on the backdrop, the × or Escape.
function RaceReadySheet({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div className="rr-sheet-backdrop" onClick={(event) => event.target === event.currentTarget && onClose()}>
      <div className="rr-sheet rr-home" role="dialog" aria-modal="true" aria-label={`Get challenge-ready for ${title || "your challenge"}`}>
        <div className="rr-sheet-head">
          <h2 className="rr-sheet-title">Get challenge-ready{title ? ` for ${title}` : ""}</h2>
          <button type="button" className="rr-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// A slim "Get challenge-ready" strip on the Home page, for the member's newest race that isn't over and isn't
// race-ready yet. Tapping it opens the full checklist in an overlay. Shows nothing when every race is ready.
export default function HomeRaceReady({ focusDashboardId = null }) {
  const { user, userRaces, userData, linkedDevice } = useUserContext();
  const [picked, setPicked] = useState(undefined);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Pick once per visit, so finishing the last step shows the celebration instead of vanishing.
    if (picked !== undefined || !(userRaces instanceof Map) || linkedDevice === null) return;
    // A reminder email's link names one challenge: that one comes first and opens even if "Not now" hid it.
    const focusLegId = focusDashboardId ? `price_${focusDashboardId.replace(/^price_/, "")}` : null;
    const candidates = groupChallengesByStatus(userRaces, Date.now(), userData?.timeZone || "").current
      .sort((a, b) => (b.legId === focusLegId) - (a.legId === focusLegId));
    let cancelled = false;

    (async () => {
      for (const race of candidates) {
        if (race.legId !== focusLegId && isHomeStripSnoozed(race?.raceReady)) continue;
        try {
          const loaded = await loadRace(race);
          if (!getProgress(race, loaded, linkedDevice).complete) {
            if (!cancelled) {
              setPicked(loaded);
              if (race.legId === focusLegId) setOpen(true);
            }
            return;
          }
        } catch (error) {
          console.error("Could not load race-ready checklist", error);
        }
      }
      if (!cancelled) setPicked(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [picked, userRaces, userData?.timeZone, linkedDevice, focusDashboardId]);

  const race = picked && userRaces?.get?.(picked.legId);
  if (!race) return null;

  const progress = getProgress(race, picked, linkedDevice);
  const logoUrl = race.raceInfo?.imageUrl;

  const snooze = () => {
    const snoozes = (Number(race.raceReady?.homeSnoozes) || 0) + 1;
    saveRaceReady(user?.uid, race.legId, { homeSnoozes: snoozes, homeSnoozedAt: "now" });
    trackRaceReady("race_ready_strip_snoozed", { race: picked.raceData?.stripe_metadata_url, snoozes, done: progress.done, total: progress.total });
    setPicked(null);
  };

  return (
    <div className="rr-home mb-2">
      <button
        type="button"
        className={`rr-strip ${progress.complete ? "is-complete" : ""}`}
        aria-haspopup="dialog"
        onClick={() => {
          if (!open) trackRaceReady("race_ready_strip_opened", { race: picked.raceData?.stripe_metadata_url, done: progress.done, total: progress.total });
          setOpen(true);
        }}
      >
        {logoUrl && <img className="rr-strip-logo" src={logoUrl} alt="" />}
        <span className="rr-strip-body">
          <span className="rr-strip-title">
            {progress.complete ? "You're challenge-ready! 🎉" : `Get challenge-ready for ${race.raceInfo?.name || "your challenge"}`}
          </span>
          <span className="rr-strip-meta">
            <span className="rr-strip-dots" aria-label={`${progress.done} of ${progress.total} steps done`}>
              {progress.required.map((step, index) => (
                <span key={step.id} className={`rr-strip-dot ${step.done ? "is-done" : ""} ${step.id === progress.nextStepId ? "is-next" : ""}`}>
                  {step.done ? "✓" : index + 1}
                </span>
              ))}
            </span>
            {progress.nextStepId && <span className="rr-strip-next">Next: {NEXT_STEP_LABELS[progress.nextStepId]}</span>}
          </span>
        </span>
        <span className="rr-strip-cta">{progress.complete ? "View" : "Continue"}</span>
      </button>
      {!progress.complete && canSnoozeHomeStrip(race.raceInfo) && (
        <button type="button" className="rr-strip-snooze" onClick={snooze}>
          Not now
        </button>
      )}

      {open &&
        createPortal(
          <RaceReadySheet title={race.raceInfo?.name} onClose={() => setOpen(false)}>
          <RaceReadyChecklist
            selectedRace={race}
            raceData={picked.raceData}
            raceName={picked.raceData?.stripe_metadata_url}
            imagePreset={picked.imagePreset}
            inviteTheme={getWelcomeTheme(picked.raceData)}
            place="home"
            onPosted={() => setOpen(false)}
            showHeader={false}
          />
          </RaceReadySheet>,
          document.body
        )}
    </div>
  );
}
